import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
    ClipboardList, ChevronRight, CheckCircle2, Circle, AlertCircle,
    Camera, Trash2, Plus, Home, Zap, Layers, Cable, MapPin, StickyNote,
    ShieldCheck, Loader2, Image as ImageIcon, Check,
} from "lucide-react";

import InstallerShell from "@/components/installer/InstallerShell";
import BackButton from "@/components/BackButton";
import { api } from "@/lib/api";
import { formatApiError, normaliseApiError } from "@/lib/apiError";

/**
 * DEV-010B-2 · Engineer digital-survey capture — Web/iPad.
 *
 * Peer of the DEV-010B Mobile capture (`/app/mobile/app/installer/
 * survey/[projectId].tsx`). Both drive the exact same backend record
 * via `/api/projects/:id/survey` + `/api/surveys/:id` + `/api/elevations`
 * + `/api/survey-photos`. No Web-only persistence, validation, lifecycle
 * or duplicate implementation — the backend is authoritative.
 *
 * Layout: two-column on desktop (section navigator ↔ active section).
 * Collapses to a single-column stack on iPad portrait / <1024px so the
 * whole workflow works on narrower screens.
 */

const ADDL_OPTIONS = ["bird_netting", "wifi_booster", "ev_charger", "iboost", "optimisers", "other"];
const ORIENTATIONS = ["N", "NE", "E", "SE", "S", "SW", "W", "NW"];
// DEV-010H UAT corrective pass · roof coverings realigned with backend
// enum (rosemary/slate/concrete/tile/flat/metal/other) + N/A sentinel.
const ROOFS = ["rosemary", "slate", "concrete", "tile", "flat", "metal", "na", "other"];
// DEV-010H · Every user-facing enum where the corrective matrix accepts
// N/A now surfaces an explicit N/A choice at the end. This is the only
// way to answer "does not apply" — blank is never treated as N/A.
const CONNECTION_POINTS = ["mains", "consumer_unit", "na", "other"];
const SURGE_OPTS = ["yes", "no", "unsure", "na"];
const CU_OPTS = ["internal", "external", "na", "other"];
const PANEL_METHOD = ["integrated", "on_rails", "na", "other"];
const RAIL_COLOUR = ["black", "silver", "na", "other"];
const SCAFFOLDING_OPTS = ["yes", "no", "unsure", "na"];
// DEV-010F · Enum realignment against the backend + Pearl survey sheet.
// Web previously offered `unsure` for COPEX and Web-only cable types
// (twin_and_earth / swa / fp200) that the backend rejects.
const COPEX = [["yes", "Yes"], ["no", "No"], ["na", "N/A"]];
const CONDUIT_COLOURS = [["white", "White"], ["black", "Black"], ["na", "N/A"], ["other", "Other"]];
const CABLE_TYPES = [["6mm_twin_earth", "6mm Twin & Earth"], ["h07", "H07"], ["na", "N/A"], ["other", "Other"]];

const SECTIONS = [
    { key: "additional_options", label: "Additional options", icon: Layers },
    { key: "electrical",         label: "Electrical connection", icon: Zap },
    { key: "roof_elevations",    label: "Roof / elevations",     icon: Home },
    { key: "panel_installation", label: "Panel installation",    icon: Layers },
    { key: "cable_conduit",      label: "Cable & conduit",       icon: Cable },
    { key: "equipment_location", label: "Equipment location",    icon: MapPin },
    { key: "additional_notes",   label: "Additional notes",      icon: StickyNote },
];

const STATUS_STYLES = {
    not_started: { label: "Not started",                bg: "#F1F5F9", fg: "#334155" },
    in_progress: { label: "In progress",                bg: "#FEF3C7", fg: "#B45309" },
    returned_for_clarification: { label: "Returned for clarification", bg: "#FEF2F2", fg: "#B91C1C" },
    submitted:   { label: "Submitted",                  bg: "#EFF6FF", fg: "#1D4ED8" },
    approved:    { label: "Approved",                   bg: "#F1F8EC", fg: "#166534" },
};

/**
 * DEV-010H UAT corrective pass 2 · Wraps a form control and adds a
 * red-ring visual state + inline "Required" hint sourced directly
 * from the canonical `/completion` `missing[]` payload. Zero
 * duplication of mandatory-field rules on the client.
 */
function FieldMissing({ missing, children, testid }) {
    if (!missing) return children;
    // DEV-010H UAT corrective pass 3 · Wording derived from canonical
    // `kind` + `allow_na`. Text/number entry fields say "enter a value"
    // while enum/bool fields say "select a value" — no client-side
    // matrix, no DOM introspection.
    const kind = missing.kind || "text";
    const isEntry = /^(text|number)(_na)?$/.test(kind);
    const hint = !missing.allow_na
        ? "Required"
        : isEntry
            ? "Required — enter a value or select N/A"
            : "Required — select a value or N/A";
    return (
        <div
            className="rounded-lg -m-1 p-1 ring-2 ring-[#DC2626] ring-offset-1 ring-offset-white bg-[#FEF2F2]/40 transition-colors"
            data-testid={testid || `field-missing-${missing.field}`}
            data-missing="true"
        >
            {children}
            <div className="mt-1 text-[11px] text-[#B91C1C] font-medium" data-testid={`${testid || `field-missing-${missing.field}`}-hint`}>
                {hint}
            </div>
        </div>
    );
}

const labelize = (v) => String(v || "").replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// DEV-010H UAT corrective pass · Human label for a canonical section key
// so validation panels render "Roof / elevations → Surveyed panel qty"
// instead of the raw underscore-y section key.
const sectionLabel = (k) => {
    const row = SECTIONS.find((s) => s.key === k);
    return row ? row.label : labelize(k);
};

function fmt(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    if (isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

// DEV-010H UAT corrective pass · `sectionDone` client-side heuristic
// deleted. The canonical backend completion engine is the single
// authority for section state — clients derive their UX from
// `/api/surveys/{id}/completion` (see the `completion` state below).

export default function InstallerSurveyCapture() {
    const { projectId } = useParams();
    const [search] = useSearchParams();
    const navigate = useNavigate();
    const appointmentId = search.get("appointment_id") || null;

    const [survey, setSurvey] = useState(null);
    const [project, setProject] = useState(null);
    const [elevations, setElevations] = useState([]);
    const [photos, setPhotos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [saving, setSaving] = useState(null);       // key of section currently saving
    const [submitting, setSubmitting] = useState(false);
    const [signoff, setSignoff] = useState(false);
    const [activeSection, setActiveSection] = useState("additional_options");
    const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
    const [showLightbox, setShowLightbox] = useState(null); // photo id
    const [approvedSpec, setApprovedSpec] = useState(null);
    const [showResubmit, setShowResubmit] = useState(false);
    const [resubmitResponse, setResubmitResponse] = useState("");
    // DEV-010H · Canonical completion state — powers "X required fields
    // outstanding" pill + missing-field navigation. Refreshed after every
    // successful save and after failed /submit attempts.
    const [completion, setCompletion] = useState(null);
    const [missingOpen, setMissingOpen] = useState(false);
    // DEV-010H UAT corrective pass 3 · Explicit tick that every save
    // path bumps. Drives the `/completion` refetch so an in-place
    // elevation update (measurements, access_constraints, scaffolding
    // notes, roof_covering) refreshes canonical state — the prior
    // `elevations.length` dep only caught add/remove.
    const [completionTick, setCompletionTick] = useState(0);
    const bumpCompletion = () => setCompletionTick((n) => n + 1);
    // DEV-010H UAT corrective pass · Previous/Next navigation across the
    // structured clarification targets + canonical missing items so the
    // Engineer can walk through every issue Ops raised. `issueIdx` is
    // the pointer into the combined queue.
    const [issueIdx, setIssueIdx] = useState(0);
    // DEV-010H UAT corrective pass · "Review & amend" now toggles an
    // explicit amendment banner so the Engineer sees they have moved
    // into edit mode and can jump to each issue with Prev/Next.
    const [amendMode, setAmendMode] = useState(false);

    const readOnly = survey?.status === "submitted" || survey?.status === "approved";
    const openClarification = (survey?.clarifications || []).find((c) => !c.resolved_at);
    const isReturned = survey?.status === "returned_for_clarification";

    // DEV-010F · When a returned survey is opened and the clarification
    // references a specific `section`, land the Engineer on that section
    // so they can review/amend before resubmitting. We only auto-navigate
    // once per clarification (tracked by id) so a subsequent manual
    // section switch is respected.
    const [autoNavigatedFor, setAutoNavigatedFor] = useState(null);
    useEffect(() => {
        if (!isReturned || !openClarification) return;
        if (autoNavigatedFor === openClarification.id) return;
        const sect = openClarification.section;
        // Map any `equipment_location_*` sub-key back to the top-level section.
        const normalise = (k) => {
            if (!k) return null;
            if (k.startsWith("equipment_location")) return "equipment_location";
            return k;
        };
        const target = normalise(sect);
        if (target && SECTIONS.some((s) => s.key === target)) {
            setActiveSection(target);
        }
        setAutoNavigatedFor(openClarification.id);
    }, [isReturned, openClarification, autoNavigatedFor]);

    // If clarification points at an elevation, scroll it into view after
    // navigation lands on the roof_elevations tab.
    useEffect(() => {
        if (!isReturned || !openClarification?.elevation_id) return;
        if (activeSection !== "roof_elevations") return;
        const el = document.querySelector(`[data-elevation-id="${openClarification.elevation_id}"]`);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [activeSection, isReturned, openClarification]);

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [s, p] = await Promise.all([
                api.createOrOpenSurvey(projectId, appointmentId),
                api.getProject(projectId),
            ]);
            setSurvey(s);
            setElevations(s?.elevations || []);
            setProject(p);
            try { setPhotos(await api.listSurveyPhotos(s.id)); } catch { setPhotos([]); }
        } catch (e) {
            setError(e?.response?.data?.detail || e?.message || "Could not open survey");
        } finally { setLoading(false); }
    }, [projectId, appointmentId]);

    useEffect(() => { load(); }, [load]);

    // DEV-010C · surface the approved installation specification when
    // the survey has been approved.
    useEffect(() => {
        if (survey?.status === "approved") {
            api.getApprovedSpecification(projectId)
                .then(setApprovedSpec)
                .catch(() => setApprovedSpec(null));
        }
    }, [projectId, survey?.status]);

    const resubmit = async () => {
        if (!openClarification || !resubmitResponse.trim()) return;
        try {
            const updated = await api.resubmitSurvey(survey.id, {
                clarification_id: openClarification.id,
                response: resubmitResponse,
            });
            setSurvey({ ...survey, ...updated });
            setShowResubmit(false);
            setResubmitResponse("");
            setCompletion(null);
        } catch (e) {
            // DEV-010H UAT corrective pass · The /resubmit endpoint returns
            // a structured survey_incomplete (422) whose `detail` is an
            // object. Rendering that object via alert() produced the
            // literal "[object Object]" that UAT reproduced. Now: normalise
            // the error, and on survey_incomplete replay the same inline
            // missing-list + navigation as /submit.
            const n = normaliseApiError(e);
            if (n.code === "survey_incomplete" && Array.isArray(n.missing)) {
                setCompletion({
                    state: "incomplete",
                    missing: n.missing,
                    section_state: {},
                    section_missing: {},
                    signoff_ok: false,
                    signoff_required: true,
                });
                setShowResubmit(false);
                setMissingOpen(true);
                setIssueIdx(0);
                const first = n.missing.find((m) => m.field !== "signoff.confirmed");
                if (first) jumpToMissing(first);
                alert(`${n.missing.length} required field(s) outstanding. Please fix them and try again.`);
            } else {
                alert(n.message);
            }
        }
    };

    const patchSection = async (key, value) => {
        if (!survey || readOnly) return;
        setSaving(key);
        try {
            const updated = await api.updateSurvey(survey.id, { [key]: value });
            setSurvey({ ...survey, ...updated });
            bumpCompletion();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setSaving(null); }
    };

    const refreshPhotos = async () => {
        try { setPhotos(await api.listSurveyPhotos(survey.id)); } catch { /* keep */ }
    };

    const attachPhoto = async (file, meta) => {
        if (!survey || readOnly || !file) return;
        setSaving("photo");
        try {
            const doc = await api.uploadProjectDocument(projectId, file, {
                title: `Survey · ${labelize(meta.section)}`,
                category: "Site Survey",
                visibility: "internal",
            });
            await api.attachSurveyPhoto(survey.id, {
                document_id: doc.id,
                section: meta.section,
                elevation_id: meta.elevationId || undefined,
                evidence_category: meta.evidence || undefined,
            });
            await refreshPhotos();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setSaving(null); }
    };

    const removePhoto = async (docId) => {
        if (readOnly) return;
        if (!window.confirm("Remove this photo from the survey?")) return;
        setSaving("photo");
        try {
            await api.detachSurveyPhoto(docId);
            await refreshPhotos();
        } catch (e) {
            alert(formatApiError(e));
        } finally { setSaving(null); }
    };

    const addElevation = async () => {
        if (!survey || readOnly) return;
        setSaving("elevations");
        try {
            const e = await api.createElevation(survey.id, {
                name: `Elevation ${elevations.length + 1}`,
                orientation: "S",
                roof_covering: "slate",
            });
            setElevations([...elevations, e]);
        } catch (er) {
            alert(formatApiError(er));
        } finally { setSaving(null); }
    };

    const updateElev = async (id, body) => {
        if (readOnly) return;
        setSaving("elevations");
        try {
            const updated = await api.updateElevation(id, body);
            setElevations(elevations.map((e) => (e.id === id ? updated : e)));
            bumpCompletion();
        } catch (er) {
            // DEV-010G · Bubble up so the ElevationCard's Save action
            // can flip into the `error` state and surface the message.
            throw er;
        } finally { setSaving(null); }
    };

    const removeElev = async (id) => {
        if (readOnly) return;
        if (!window.confirm("Remove this elevation? This cannot be undone.")) return;
        setSaving("elevations");
        try {
            await api.deleteElevation(id);
            setElevations(elevations.filter((e) => e.id !== id));
        } catch (er) {
            alert(formatApiError(er));
        } finally { setSaving(null); }
    };

    const submit = async () => {
        if (!signoff) return;
        setSubmitting(true);
        try {
            const updated = await api.submitSurvey(survey.id, { signoff_confirmed: true });
            setSurvey({ ...survey, ...updated });
            setShowSubmitConfirm(false);
            setCompletion(null);
        } catch (e) {
            // DEV-010H · Backend returns a structured 422 with `missing[]`.
            // Show the missing list inline and land the Engineer on the
            // first missing section so they can fix it fast.
            const n = normaliseApiError(e);
            if (n.code === "survey_incomplete" && Array.isArray(n.missing)) {
                setCompletion({
                    state: "incomplete",
                    missing: n.missing,
                    section_state: {},
                    section_missing: {},
                    signoff_ok: false,
                    signoff_required: true,
                });
                setShowSubmitConfirm(false);
                setMissingOpen(true);
                setIssueIdx(0);
                // Auto-navigate to the first missing section.
                const first = n.missing.find((m) => m.field !== "signoff.confirmed");
                if (first) jumpToMissing(first);
            } else {
                alert(n.message);
            }
        } finally { setSubmitting(false); }
    };

    // DEV-010H · Fetch canonical completion state whenever the survey or
    // elevations change so the "X required fields outstanding" pill stays
    // in sync with the backend authority.
    //
    // DEV-010H UAT corrective pass · We now ALSO fetch for surveys in
    // `returned_for_clarification` state — previously skipped, which is
    // why physical UAT showed a returned survey with no missing-field
    // guidance. The canonical engine remains the single authority for
    // both submit and resubmit gates.
    useEffect(() => {
        if (!survey?.id) return;
        if (survey?.status === "approved") return;
        let cancelled = false;
        (async () => {
            try {
                const body = await api.getSurveyCompletion(survey.id);
                if (!cancelled) setCompletion(body);
            } catch { /* silent — pill just stays hidden */ }
        })();
        return () => { cancelled = true; };
    }, [survey?.id, survey?.updated_at, survey?.status, elevations.length, completionTick]);

    // Missing-field click → navigate to the section (and scroll to the
    // elevation card when applicable).
    const jumpToMissing = (m) => {
        if (m?.section && SECTIONS.some((s) => s.key === m.section)) {
            setActiveSection(m.section);
        }
        if (m?.elevation_id) {
            setTimeout(() => {
                const el = document.querySelector(`[data-elevation-id="${m.elevation_id}"]`);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            }, 200);
        }
        setMissingOpen(false);
    };

    // DEV-010H UAT corrective pass 2 · Map every canonical missing item
    // to a `${field}::${elevation_id || ""}` key so per-field renderers
    // can visually flag the exact control that is outstanding. This is
    // the ONLY client-side mandatory-field state and it is driven
    // entirely by the backend `/completion` response — never a second
    // matrix.
    const missingByKey = useMemo(() => {
        const map = new Map();
        for (const m of (completion?.missing || [])) {
            if (!m || !m.field) continue;
            map.set(`${m.field}::${m.elevation_id || ""}`, m);
        }
        return map;
    }, [completion]);
    const isMissing = (field, elevationId) => missingByKey.has(`${field}::${elevationId || ""}`);
    const missingFor = (field, elevationId) => missingByKey.get(`${field}::${elevationId || ""}`);

    const donePerSection = useMemo(() => {
        // DEV-010H UAT corrective pass · A section is "done" ONLY when the
        // canonical backend engine says its state is `complete`. The old
        // client-side "has any content" heuristic is dead — it produced
        // the false 100%/complete display that UAT reproduced.
        const state = completion?.section_state || {};
        const counts = {};
        for (const s of SECTIONS) counts[s.key] = state[s.key] === "complete";
        return counts;
    }, [completion]);
    const doneCount = Object.values(donePerSection).filter(Boolean).length;
    const progressPct = Math.round((doneCount / SECTIONS.length) * 100);

    // Combined issue queue for Previous/Next navigation on returned
    // surveys — clarification targets first (Ops asked about these),
    // then canonical missing fields (independent validation). Duplicates
    // (same section+field+elevation) are collapsed onto the target row.
    const issueQueue = useMemo(() => {
        const rows = [];
        const seen = new Set();
        const key = (o) => `${o.section || ""}::${o.field || ""}::${o.elevation_id || ""}`;
        for (const t of openClarification?.targets || []) {
            const k = key({ section: t.section, field: t.field, elevation_id: t.elevation_id });
            if (seen.has(k)) continue;
            seen.add(k);
            rows.push({ ...t, source: "clarification" });
        }
        for (const m of completion?.missing || []) {
            if (m.field === "signoff.confirmed") continue;
            const k = key(m);
            if (seen.has(k)) continue;
            seen.add(k);
            rows.push({ ...m, source: "validation" });
        }
        return rows;
    }, [openClarification, completion]);

    // DEV-010G · The total panel quantity is derived from the sum of
    // surveyed panels across every saved elevation. Rendered read-only
    // in the Panel installation section AND pushed back into the survey
    // whenever it changes so the backend has a consistent value.
    const derivedPanelQty = useMemo(() =>
        elevations.reduce((sum, e) => sum + (Number(e.surveyed_panel_qty) || 0), 0),
        [elevations]);
    useEffect(() => {
        if (!survey || readOnly) return;
        const current = survey.panel_installation?.panel_quantity ?? null;
        if (current === derivedPanelQty) return;
        // Persist silently (no toast) — this is a derived value.
        (async () => {
            try {
                const updated = await api.updateSurvey(survey.id, {
                    panel_installation: {
                        ...survey.panel_installation,
                        panel_quantity: derivedPanelQty,
                    },
                });
                setSurvey((s) => s ? { ...s, ...updated } : s);
            } catch { /* ignore — save-error handled elsewhere */ }
        })();
    }, [derivedPanelQty, survey?.id, readOnly]);      // eslint-disable-line react-hooks/exhaustive-deps

    if (loading) return (
        <InstallerShell>
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 text-center text-sm text-[#4B5565]">
                <Loader2 size={20} className="inline-block animate-spin mr-2" /> Loading survey…
            </div>
        </InstallerShell>
    );

    if (error) return (
        <InstallerShell>
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-16 text-center">
                <AlertCircle size={28} className="mx-auto text-[#B91C1C]" />
                <div className="mt-3 text-lg text-[#131B2F] font-medium">Couldn&rsquo;t open the survey</div>
                <div className="text-sm text-[#4B5565] mt-1">{error}</div>
                <button onClick={() => navigate("/installer/surveys")} className="mt-4 h-9 px-4 rounded-full bg-[#131B2F] text-white text-sm font-medium">Back to surveys</button>
            </div>
        </InstallerShell>
    );

    const st = STATUS_STYLES[survey.status] || STATUS_STYLES.not_started;
    const proposed = survey.proposed_snapshot || {};

    const sectionPhotos = (section, elevationId = null) =>
        (photos || []).filter((p) =>
            p.survey_section === section &&
            (elevationId ? p.elevation_id === elevationId : !p.elevation_id)
        );

    return (
        <InstallerShell>
            <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-6 md:py-8" data-testid="installer-survey-capture">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <BackButton
                        to="/installer/surveys"
                        label="Back"
                        testid="survey-back"
                    />
                    <div className="flex-1 min-w-0">
                        <div className="text-xs text-[#4B5565] uppercase tracking-[0.12em] font-medium">Digital site survey</div>
                        <h1 className="text-2xl md:text-3xl text-[#131B2F] font-medium truncate" data-testid="survey-project-title">
                            {project?.customer_name || "—"}
                        </h1>
                        {project?.address && <div className="text-sm text-[#4B5565] mt-0.5 truncate">{project.address}</div>}
                    </div>
                    <span
                        className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full"
                        style={{ backgroundColor: st.bg, color: st.fg }}
                        data-testid="survey-status-pill"
                    >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: st.fg }} />
                        {st.label}
                    </span>
                </div>

                {readOnly && (
                    <div className="mb-6 rounded-xl border border-[#DBEAFE] bg-[#EFF6FF] p-4 flex items-start gap-3" data-testid="survey-readonly-banner">
                        <ShieldCheck size={18} className="text-[#1D4ED8] mt-0.5" strokeWidth={1.75} />
                        <div className="text-sm text-[#1E3A8A]">
                            This survey has been {survey.status}. It is now read-only.
                            {survey.submitted_at && <> Submitted {fmt(survey.submitted_at)}.</>}
                        </div>
                    </div>
                )}

                {isReturned && openClarification && (
                    <div className="mb-6 rounded-xl border border-[#FED7AA] bg-[#FFF7ED] p-4" data-testid="clarification-banner">
                        <div className="flex items-start gap-3">
                            <AlertCircle size={18} className="text-[#B45309] mt-0.5" strokeWidth={1.75} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[#7C2D12]">
                                    Operations returned this survey for clarification
                                </div>
                                <div className="text-xs text-[#4B5565] mt-0.5">
                                    {openClarification.requested_by_name || "Ops"} · {fmt(openClarification.requested_at)}
                                </div>
                                <div className="text-sm text-[#131B2F] mt-2 whitespace-pre-wrap">
                                    {openClarification.text}
                                </div>
                                {/* DEV-010H · Show every pinned target so
                                    the Engineer knows exactly which items
                                    Ops is asking about. Each chip is a
                                    click-through to that section (and
                                    scrolls to the specific elevation card
                                    when applicable). */}
                                {(openClarification.targets || []).length > 0 && (
                                    <ul className="mt-2 flex flex-wrap gap-1" data-testid="clarification-targets">
                                        {openClarification.targets.map((t, i) => (
                                            <li key={i}>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        const target = t.section && t.section.startsWith("equipment_location")
                                                            ? "equipment_location" : t.section;
                                                        if (target && SECTIONS.some((s) => s.key === target)) {
                                                            setActiveSection(target);
                                                        }
                                                        if (t.elevation_id) {
                                                            setTimeout(() => {
                                                                const el = document.querySelector(`[data-elevation-id="${t.elevation_id}"]`);
                                                                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
                                                            }, 200);
                                                        }
                                                    }}
                                                    data-testid={`clarification-target-${i}`}
                                                    className="text-[11px] px-2 py-0.5 rounded-full bg-white text-[#7C2D12] border border-[#FED7AA] hover:bg-[#FEF3C7]"
                                                >
                                                    {t.label || [t.section, t.field].filter(Boolean).join(" · ")}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                                {/* DEV-010F · The primary CTA now takes the
                                    Engineer to the relevant section for
                                    editing. The final "Resubmit for review"
                                    action is a separate control that lives on
                                    the sticky footer so the Engineer must
                                    review/amend before resubmitting. */}
                                <div className="mt-3 flex flex-wrap gap-2 items-center">
                                    <button
                                        type="button"
                                        onClick={() => {
                                            // DEV-010H UAT corrective pass · Explicitly
                                            // enter amendment mode + surface the
                                            // combined issue queue. Navigate to the
                                            // first issue (target OR canonical
                                            // missing) so the click always visibly
                                            // does something.
                                            setAmendMode(true);
                                            setMissingOpen(true);
                                            setIssueIdx(0);
                                            const first = issueQueue[0];
                                            if (first) {
                                                jumpToMissing(first);
                                            } else if (openClarification?.section) {
                                                const sect = openClarification.section;
                                                const target = sect.startsWith("equipment_location") ? "equipment_location" : sect;
                                                if (SECTIONS.some((s) => s.key === target)) setActiveSection(target);
                                            }
                                        }}
                                        data-testid="review-amend-btn"
                                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110"
                                    >
                                        {amendMode ? "Continue amending" : "Review & amend survey"}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setShowResubmit(true)}
                                        data-testid="resubmit-btn"
                                        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium bg-white text-[#131B2F] border border-[#E5E7EB] hover:bg-[#F9FAFB]"
                                    >
                                        Resubmit for review
                                    </button>
                                    {/* DEV-010H UAT corrective pass · Previous/Next
                                        navigation across every clarification
                                        target + canonical missing field. Only
                                        rendered when there is more than one
                                        issue to walk through. */}
                                    {issueQueue.length > 1 && (
                                        <div className="inline-flex items-center gap-1 ml-auto" data-testid="issue-nav">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const next = Math.max(0, issueIdx - 1);
                                                    setIssueIdx(next);
                                                    jumpToMissing(issueQueue[next]);
                                                }}
                                                disabled={issueIdx <= 0}
                                                data-testid="issue-prev"
                                                className="h-8 px-3 rounded-full text-xs font-medium bg-white text-[#131B2F] border border-[#E5E7EB] disabled:opacity-40"
                                            >
                                                ‹ Previous issue
                                            </button>
                                            <span className="text-[11px] text-[#4B5565] tabular-nums" data-testid="issue-idx">
                                                {issueIdx + 1} / {issueQueue.length}
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    const next = Math.min(issueQueue.length - 1, issueIdx + 1);
                                                    setIssueIdx(next);
                                                    jumpToMissing(issueQueue[next]);
                                                }}
                                                disabled={issueIdx >= issueQueue.length - 1}
                                                data-testid="issue-next"
                                                className="h-8 px-3 rounded-full text-xs font-medium bg-white text-[#131B2F] border border-[#E5E7EB] disabled:opacity-40"
                                            >
                                                Next issue ›
                                            </button>
                                        </div>
                                    )}
                                </div>
                                {/* DEV-010H UAT corrective pass · When canonical
                                    validation surfaces missing fields that are
                                    NOT what Ops asked about, we display them as
                                    a distinct block below the Ops comment so the
                                    Engineer can see both concerns clearly. */}
                                {(completion?.missing || []).filter((m) => m.field !== "signoff.confirmed").length > 0 && (
                                    <div className="mt-3 rounded-md border border-[#FCA5A5] bg-[#FEF2F2] p-2" data-testid="returned-missing-panel">
                                        <div className="text-xs font-medium text-[#7F1D1D]">
                                            Required survey information outstanding:
                                        </div>
                                        <ul className="mt-1 space-y-0.5" data-testid="returned-missing-list">
                                            {(completion?.missing || [])
                                                .filter((m) => m.field !== "signoff.confirmed")
                                                .map((m, i) => (
                                                <li key={`${m.field}-${m.elevation_id || i}`}>
                                                    <button
                                                        type="button"
                                                        onClick={() => jumpToMissing(m)}
                                                        data-testid={`returned-missing-item-${i}`}
                                                        className="text-[11.5px] text-[#7F1D1D] hover:underline"
                                                    >
                                                        · {sectionLabel(m.section)}{m.elevation_name ? ` — ${m.elevation_name}` : ""} → {m.label}
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                )}

                {approvedSpec && (
                    <div className="mb-6 rounded-xl border border-[#DCFCE7] bg-[#F7FBF4] p-4" data-testid="approved-spec-banner">
                        <div className="flex items-start gap-3">
                            <ShieldCheck size={18} className="text-[#166534] mt-0.5" strokeWidth={1.75} />
                            <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-[#166534]">
                                    Approved installation specification (Version {approvedSpec.version})
                                </div>
                                <div className="text-xs text-[#4B5565] mt-0.5">
                                    {approvedSpec.approved_by_name || "Ops"} · {fmt(approvedSpec.approved_at)}
                                </div>
                                <ul className="mt-2 text-xs space-y-1" data-testid="engineer-approved-spec-fields">
                                    {[
                                        ["Panel quantity", approvedSpec.specification?.panel_quantity],
                                        ["Installation method", approvedSpec.specification?.panel_installation_method],
                                        ["Rail colour", approvedSpec.specification?.rail_colour],
                                        ["Additional options", approvedSpec.specification?.additional_options],
                                        ["Inverter location", approvedSpec.specification?.inverter_location],
                                        ["Battery location", approvedSpec.specification?.battery_location],
                                    ].map(([label, cell]) => {
                                        if (!cell || cell.value == null || cell.value === "") return null;
                                        const v = Array.isArray(cell.value) ? cell.value.join(", ") : String(cell.value);
                                        return (
                                            <li key={label} className="flex gap-2">
                                                <span className="text-[#4B5565] w-36 shrink-0">{label}</span>
                                                <span className="text-[#131B2F] font-medium">{v}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        </div>
                    </div>
                )}

                <div className="grid grid-cols-1 lg:grid-cols-[280px_minmax(0,1fr)] gap-6">
                    {/* Section navigator */}
                    <aside className="lg:sticky lg:top-6 lg:self-start space-y-3">
                        <div className="pe-card p-4">
                            <div className="text-xs uppercase tracking-[0.12em] text-[#4B5565] font-medium">Progress</div>
                            <div className="mt-1 flex items-center gap-3">
                                <div className="flex-1 h-2 rounded-full bg-[#F1F5F9] overflow-hidden">
                                    <div className="h-full bg-[#3E9E2E] transition-all" style={{ width: `${progressPct}%` }} data-testid="survey-progress-bar" />
                                </div>
                                <div className="text-sm font-medium text-[#131B2F] tabular-nums" data-testid="survey-progress-pct">{progressPct}%</div>
                            </div>
                            <div className="text-xs text-[#4B5565] mt-1" data-testid="survey-progress-label">
                                {doneCount}/{SECTIONS.length} sections complete
                            </div>
                        </div>
                        <nav className="pe-card p-2" data-testid="survey-nav">
                            {SECTIONS.map((s) => {
                                const Icon = s.icon;
                                const active = activeSection === s.key;
                                const done = donePerSection[s.key];
                                return (
                                    <button
                                        key={s.key}
                                        onClick={() => setActiveSection(s.key)}
                                        data-testid={`survey-nav-${s.key}`}
                                        className={"w-full text-left flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-colors " +
                                            (active ? "bg-[#131B2F] text-white" : "text-[#131B2F] hover:bg-[#F9FAFB]")}
                                    >
                                        <span className={"shrink-0 w-6 h-6 rounded-md flex items-center justify-center " + (active ? "bg-white/15 text-white" : done ? "bg-[#F1F8EC] text-[#166534]" : "bg-[#F1F5F9] text-[#4B5565]")}>
                                            {done ? <CheckCircle2 size={13} strokeWidth={2.25} /> : <Icon size={13} strokeWidth={1.75} />}
                                        </span>
                                        <span className="flex-1 truncate">{s.label}</span>
                                    </button>
                                );
                            })}
                        </nav>

                        {/* Sign-off + Submit — visible when editable and >=1 elevation.
                            DEV-010G · When the survey is returned_for_clarification
                            the ordinary Submit path is intentionally suppressed so
                            the Engineer must go through Resubmit for review (which
                            captures the clarification response and calls the
                            authoritative /resubmit endpoint). */}
                        {!readOnly && !isReturned && (
                            <div className="pe-card p-4 space-y-3" data-testid="survey-signoff-card">
                                <div className="text-xs uppercase tracking-[0.12em] text-[#4B5565] font-medium">Engineer sign-off</div>
                                <label className="flex items-start gap-2 text-sm text-[#131B2F] cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={signoff}
                                        onChange={(e) => { setSignoff(e.target.checked); bumpCompletion(); }}
                                        data-testid="survey-signoff-checkbox"
                                        className="mt-1"
                                    />
                                    <span>I confirm this survey accurately reflects my findings.</span>
                                </label>
                                {/* DEV-010H UAT corrective pass 3 · Canonical outstanding
                                    control. Distinguishes two independent concepts:
                                     (a) sections completed  — 7/7 = all survey fields OK,
                                     (b) survey ready to submit — sections OK AND signoff on.
                                    When the ONLY outstanding item is signoff.confirmed we
                                    explicitly say so instead of "1 required field(s)
                                    outstanding" and clicking scrolls the checkbox into
                                    view rather than a non-existent form control. */}
                                {completion && completion.state !== "complete" && (() => {
                                    const survey_missing = (completion.missing || []).filter((m) => m.field !== "signoff.confirmed");
                                    const only_signoff = survey_missing.length === 0 && !completion.signoff_ok;
                                    return (
                                        <div className="rounded-lg border border-[#FED7AA] bg-[#FFF7ED] p-3" data-testid="survey-missing-pill">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    if (only_signoff) {
                                                        // Focus / scroll the sign-off checkbox.
                                                        const el = document.querySelector('[data-testid=survey-signoff-checkbox]');
                                                        if (el) {
                                                            el.scrollIntoView({behavior: "smooth", block: "center"});
                                                            el.focus();
                                                        }
                                                        return;
                                                    }
                                                    if (survey_missing.length === 1) {
                                                        // Single issue — jump straight there.
                                                        jumpToMissing(survey_missing[0]);
                                                        setIssueIdx(0);
                                                        return;
                                                    }
                                                    setMissingOpen((v) => !v);
                                                }}
                                                data-testid="survey-missing-toggle"
                                                className="w-full flex items-center justify-between gap-2 text-xs font-medium text-[#9A3412]"
                                            >
                                                <span data-testid="survey-missing-summary">
                                                    {only_signoff
                                                        ? "Engineer sign-off outstanding"
                                                        : survey_missing.length === 1
                                                            ? `${sectionLabel(survey_missing[0].section)} → ${survey_missing[0].label}${survey_missing[0].elevation_name ? ` (${survey_missing[0].elevation_name})` : ""}`
                                                            : `${survey_missing.length} required field(s) outstanding`}
                                                </span>
                                                {!only_signoff && survey_missing.length > 1 && <span aria-hidden>{missingOpen ? "▾" : "▸"}</span>}
                                            </button>
                                            {missingOpen && survey_missing.length > 1 && (
                                                <ul className="mt-2 space-y-1" data-testid="survey-missing-list">
                                                    {survey_missing.map((m, i) => (
                                                        <li key={`${m.field}-${m.elevation_id || i}`}>
                                                            <button
                                                                type="button"
                                                                onClick={() => { jumpToMissing(m); setIssueIdx(i); }}
                                                                data-testid={`survey-missing-item-${i}`}
                                                                className="w-full text-left text-[11.5px] text-[#7C2D12] hover:text-[#431407] hover:underline"
                                                            >
                                                                {sectionLabel(m.section)} → {m.label}
                                                                {m.elevation_name ? ` (${m.elevation_name})` : ""}
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    );
                                })()}
                                <button
                                    type="button"
                                    onClick={() => setShowSubmitConfirm(true)}
                                    disabled={!signoff || elevations.length === 0
                                        || (completion && completion.state !== "complete"
                                            // signoff.confirmed is set by /submit itself
                                            && !(completion.missing || []).every((m) => m.field === "signoff.confirmed"))}
                                    data-testid="survey-submit-btn"
                                    className="w-full inline-flex items-center justify-center gap-1.5 h-10 rounded-full text-sm font-medium bg-[#3E9E2E] text-white hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                                >
                                    {completion && completion.state !== "complete"
                                        && !(completion.missing || []).every((m) => m.field === "signoff.confirmed")
                                        ? `${completion.missing?.filter((m) => m.field !== "signoff.confirmed").length} required field(s) outstanding`
                                        : "Submit survey"}
                                </button>
                                {elevations.length === 0 && (
                                    <div className="text-[11px] text-[#B45309]">Add at least one roof elevation to enable submission.</div>
                                )}
                            </div>
                        )}
                    </aside>

                    {/* Active section pane */}
                    <div className="min-w-0 space-y-6">
                        {activeSection === "additional_options" && (
                            <SectionCard
                                title="Additional options"
                                sublabel="Extras the customer has agreed to fit — tick every option that applies."
                                saving={saving === "additional_options"}
                                proposed={proposed.additional_options}
                            >
                                <MultiSelect
                                    options={ADDL_OPTIONS}
                                    value={survey.additional_options?.selected || []}
                                    disabled={readOnly}
                                    onChange={(sel) => patchSection("additional_options", { ...survey.additional_options, selected: sel })}
                                    testidPrefix="opt"
                                />
                                {(survey.additional_options?.selected || []).includes("other") && (
                                    <TextField
                                        label="Other — describe"
                                        value={survey.additional_options?.other_description || ""}
                                        onBlur={(v) => patchSection("additional_options", { ...survey.additional_options, other_description: v })}
                                        disabled={readOnly}
                                        testid="opt-other-desc"
                                    />
                                )}
                            </SectionCard>
                        )}

                        {activeSection === "electrical" && (
                            <SectionCard
                                title="Electrical connection"
                                sublabel="Where the new system ties into the existing electrical installation."
                                saving={saving === "electrical"}
                                proposed={proposed.electrical}
                            >
                                <FieldMissing missing={missingFor("electrical.connection_point")}>
                                <Segmented
                                    label="Connection point"
                                    options={CONNECTION_POINTS}
                                    value={survey.electrical?.connection_point || null}
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("electrical", { ...survey.electrical, connection_point: v })}
                                    testidPrefix="elec-cp"
                                />
                                </FieldMissing>
                                <FieldMissing missing={missingFor("electrical.distance_meters")}>
                                <NumberFieldWithNA
                                    label="Distance to connection (metres)"
                                    value={survey.electrical?.distance_meters}
                                    naValue={survey.electrical?.distance_meters_na}
                                    onChange={({ value, na }) => patchSection("electrical", {
                                        ...survey.electrical,
                                        distance_meters: value,
                                        distance_meters_na: na,
                                    })}
                                    disabled={readOnly}
                                    testid="elec-distance"
                                />
                                </FieldMissing>
                                <FieldMissing missing={missingFor("electrical.surge_protection")}>
                                <Segmented
                                    label="Surge protection"
                                    options={SURGE_OPTS}
                                    value={survey.electrical?.surge_protection || null}
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("electrical", { ...survey.electrical, surge_protection: v })}
                                    testidPrefix="elec-surge"
                                />
                                </FieldMissing>
                                <FieldMissing missing={missingFor("electrical.consumer_unit_location")}>
                                <Segmented
                                    label="Consumer unit location"
                                    options={CU_OPTS}
                                    value={
                                        // DEV-010E · Align Web with canonical
                                        // backend field `consumer_unit_location`.
                                        // Older Web-only records may have persisted
                                        // as `consumer_unit` — read that as a
                                        // safe compatibility fallback so legacy
                                        // captures still render, and rewrite on
                                        // next change.
                                        survey.electrical?.consumer_unit_location
                                        ?? survey.electrical?.consumer_unit
                                        ?? null
                                    }
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("electrical", {
                                        ...survey.electrical,
                                        consumer_unit_location: v,
                                        // Clear the legacy field so the record
                                        // stops carrying two conflicting values.
                                        consumer_unit: undefined,
                                        // Clear the free-text companion when the
                                        // location is no longer "Other".
                                        ...(v !== "other" ? { consumer_unit_location_other: undefined } : {}),
                                    })}
                                    testidPrefix="elec-cu"
                                />
                                </FieldMissing>
                                {/* DEV-010E · When "Other" is picked, surface the
                                    same free-text field mobile already uses so
                                    Engineers can describe the location. */}
                                {(survey.electrical?.consumer_unit_location === "other") && (
                                    <input
                                        type="text"
                                        placeholder="Describe the consumer unit location"
                                        value={survey.electrical?.consumer_unit_location_other || ""}
                                        onChange={(e) => patchSection("electrical", {
                                            ...survey.electrical,
                                            consumer_unit_location_other: e.target.value,
                                        })}
                                        disabled={readOnly}
                                        data-testid="elec-cu-other-text"
                                        className="mt-2 w-full h-9 rounded-lg border border-[#E5E7EB] px-3 text-sm text-[#131B2F] bg-white disabled:opacity-60"
                                    />
                                )}
                                <TextField
                                    label="Electrical notes"
                                    multiline
                                    value={survey.electrical?.notes || ""}
                                    onBlur={(v) => patchSection("electrical", { ...survey.electrical, notes: v })}
                                    disabled={readOnly}
                                    testid="elec-notes"
                                />
                                <PhotoStrip
                                    label="Electrical evidence"
                                    photos={sectionPhotos("electrical")}
                                    onUpload={(file) => attachPhoto(file, { section: "electrical" })}
                                    onRemove={removePhoto}
                                    onOpen={setShowLightbox}
                                    disabled={readOnly}
                                    testid="elec-photos"
                                />
                            </SectionCard>
                        )}

                        {activeSection === "roof_elevations" && (
                            <SectionCard
                                title="Roof / elevations"
                                sublabel="Add one entry per roof face. Panel counts, orientation and scaffolding requirements per elevation."
                                saving={saving === "elevations"}
                            >
                                {elevations.length === 0 ? (
                                    <div className="text-sm text-[#4B5565] p-4 border border-dashed border-[#E5E7EB] rounded-lg">
                                        No elevations yet. Add at least one to enable submission.
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        {elevations.map((e, idx) => (
                                            <ElevationCard
                                                key={e.id}
                                                elevation={e}
                                                index={idx}
                                                onUpdate={(body) => updateElev(e.id, body)}
                                                onRemove={() => removeElev(e.id)}
                                                photos={sectionPhotos("roof_elevations", e.id)}
                                                onPhoto={(file) => attachPhoto(file, { section: "roof_elevations", elevationId: e.id })}
                                                onPhotoRemove={removePhoto}
                                                onPhotoOpen={setShowLightbox}
                                                readOnly={readOnly}
                                                missingFor={(field) => missingFor(field, e.id)}
                                            />
                                        ))}
                                    </div>
                                )}
                                {!readOnly && (
                                    <button
                                        type="button"
                                        onClick={addElevation}
                                        data-testid="add-elevation"
                                        className="mt-3 inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium bg-[#131B2F] text-white hover:brightness-110"
                                    >
                                        <Plus size={14} strokeWidth={2.25} /> Add elevation
                                    </button>
                                )}
                            </SectionCard>
                        )}

                        {activeSection === "panel_installation" && (
                            <SectionCard
                                title="Panel installation"
                                sublabel="How the panels will be mechanically fitted to the roof."
                                saving={saving === "panel_installation"}
                                proposed={proposed.panel_installation}
                            >
                                <FieldMissing missing={missingFor("panel_installation.method")}>
                                <Segmented
                                    label="Installation method"
                                    options={PANEL_METHOD}
                                    value={survey.panel_installation?.method || null}
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("panel_installation", { ...survey.panel_installation, method: v })}
                                    testidPrefix="panel-method"
                                />
                                </FieldMissing>
                                <FieldMissing missing={missingFor("panel_installation.rail_colour")}>
                                <Segmented
                                    label="Rail colour"
                                    options={RAIL_COLOUR}
                                    value={survey.panel_installation?.rail_colour || null}
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("panel_installation", { ...survey.panel_installation, rail_colour: v })}
                                    testidPrefix="panel-rail"
                                />
                                </FieldMissing>
                                {/* DEV-010G · Derived from Roof / elevations.
                                    The authoritative surveyed total is the sum
                                    of every saved elevation's
                                    `surveyed_panel_qty` — a manually-entered
                                    value would drift the two sources of
                                    truth. Field is read-only and updates
                                    live as elevations change. */}
                                <div data-testid="panel-qty-derived">
                                    <div className="text-sm font-medium text-[#131B2F] mb-1.5">
                                        Total panel quantity
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <input
                                            type="text"
                                            value={derivedPanelQty}
                                            readOnly
                                            disabled
                                            data-testid="panel-qty"
                                            className="w-24 px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm text-[#131B2F] bg-[#F9FAFB] cursor-not-allowed"
                                        />
                                        <span className="text-xs text-[#4B5565]">
                                            Sum of surveyed panels across {elevations.length} elevation{elevations.length === 1 ? "" : "s"}
                                        </span>
                                    </div>
                                </div>
                                <TextField
                                    label="Installation notes"
                                    multiline
                                    value={survey.panel_installation?.notes || ""}
                                    onBlur={(v) => patchSection("panel_installation", { ...survey.panel_installation, notes: v })}
                                    disabled={readOnly}
                                    testid="panel-notes"
                                />
                            </SectionCard>
                        )}

                        {activeSection === "cable_conduit" && (
                            <SectionCard
                                title="Cable & conduit"
                                sublabel="Cabling route between roof array and equipment location."
                                saving={saving === "cable_conduit"}
                            >
                                <FieldMissing missing={missingFor("cable_conduit.mains_route")}>
                                <TextFieldWithNA
                                    label="Mains route"
                                    value={survey.cable_conduit?.mains_route || ""}
                                    onBlur={(v) => patchSection("cable_conduit", { ...survey.cable_conduit, mains_route: v })}
                                    disabled={readOnly}
                                    testid="cable-mains-route"
                                />
                                </FieldMissing>
                                <FieldMissing missing={missingFor("cable_conduit.dc_route")}>
                                <TextFieldWithNA
                                    label="DC route"
                                    value={survey.cable_conduit?.dc_route || ""}
                                    onBlur={(v) => patchSection("cable_conduit", { ...survey.cable_conduit, dc_route: v })}
                                    disabled={readOnly}
                                    testid="cable-dc-route"
                                />
                                </FieldMissing>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                                    <FieldMissing missing={missingFor("cable_conduit.conduit_length_m")}>
                                    <NumberFieldWithNA
                                        label="Conduit length (m)"
                                        value={survey.cable_conduit?.conduit_length_m}
                                        naValue={survey.cable_conduit?.conduit_length_m_na}
                                        onChange={({ value, na }) => patchSection("cable_conduit", {
                                            ...survey.cable_conduit,
                                            conduit_length_m: value,
                                            conduit_length_m_na: na,
                                        })}
                                        disabled={readOnly}
                                        testid="cable-length"
                                    />
                                    </FieldMissing>
                                    <FieldMissing missing={missingFor("cable_conduit.boxes_count")}>
                                    <NumberFieldWithNA
                                        label="Number of boxes"
                                        integer
                                        // DEV-010F · Canonical backend field is
                                        // `boxes_count`. Fall back to legacy
                                        // `boxes` for historical records so they
                                        // still render.
                                        value={survey.cable_conduit?.boxes_count ?? survey.cable_conduit?.boxes}
                                        naValue={survey.cable_conduit?.boxes_count_na}
                                        onChange={({ value, na }) => patchSection("cable_conduit", {
                                            ...survey.cable_conduit,
                                            boxes_count: value,
                                            boxes_count_na: na,
                                            boxes: undefined,       // stop persisting the shadow field
                                        })}
                                        disabled={readOnly}
                                        testid="cable-boxes"
                                    />
                                    </FieldMissing>
                                    <FieldMissing missing={missingFor("cable_conduit.bends_count")}>
                                    <NumberFieldWithNA
                                        label="Number of bends"
                                        integer
                                        value={survey.cable_conduit?.bends_count ?? survey.cable_conduit?.bends}
                                        naValue={survey.cable_conduit?.bends_count_na}
                                        onChange={({ value, na }) => patchSection("cable_conduit", {
                                            ...survey.cable_conduit,
                                            bends_count: value,
                                            bends_count_na: na,
                                            bends: undefined,
                                        })}
                                        disabled={readOnly}
                                        testid="cable-bends"
                                    />
                                    </FieldMissing>
                                </div>
                                {/* DEV-010F · Conduit colour is a controlled
                                    enum in the backend (white / black / other).
                                    Web previously accepted arbitrary free-text
                                    which the backend would reject on save. */}
                                <FieldMissing missing={missingFor("cable_conduit.conduit_colour")}>
                                <Segmented
                                    label="Conduit colour"
                                    options={CONDUIT_COLOURS}
                                    value={survey.cable_conduit?.conduit_colour || null}
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("cable_conduit", {
                                        ...survey.cable_conduit,
                                        conduit_colour: v,
                                        ...(v !== "other" ? { conduit_colour_other: undefined } : {}),
                                    })}
                                    testidPrefix="cable-colour"
                                />
                                </FieldMissing>
                                {survey.cable_conduit?.conduit_colour === "other" && (
                                    <TextField
                                        label="Conduit colour — describe"
                                        value={survey.cable_conduit?.conduit_colour_other || ""}
                                        onBlur={(v) => patchSection("cable_conduit", { ...survey.cable_conduit, conduit_colour_other: v })}
                                        disabled={readOnly}
                                        testid="cable-colour-other-text"
                                    />
                                )}
                                <FieldMissing missing={missingFor("cable_conduit.copex_required")}>
                                <Segmented
                                    label="COPEX required"
                                    options={COPEX}
                                    value={survey.cable_conduit?.copex_required || null}
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("cable_conduit", { ...survey.cable_conduit, copex_required: v })}
                                    testidPrefix="cable-copex"
                                />
                                </FieldMissing>
                                <FieldMissing missing={missingFor("cable_conduit.cable_type")}>
                                <Segmented
                                    label="Cable type"
                                    options={CABLE_TYPES}
                                    value={survey.cable_conduit?.cable_type || null}
                                    disabled={readOnly}
                                    onChange={(v) => patchSection("cable_conduit", {
                                        ...survey.cable_conduit,
                                        cable_type: v,
                                        ...(v !== "other" ? { cable_type_other: undefined } : {}),
                                    })}
                                    testidPrefix="cable-type"
                                />
                                </FieldMissing>
                                {survey.cable_conduit?.cable_type === "other" && (
                                    <TextField
                                        label="Cable type — describe"
                                        value={survey.cable_conduit?.cable_type_other || ""}
                                        onBlur={(v) => patchSection("cable_conduit", { ...survey.cable_conduit, cable_type_other: v })}
                                        disabled={readOnly}
                                        testid="cable-type-other-text"
                                    />
                                )}
                                <TextField
                                    label="Cable & conduit notes"
                                    multiline
                                    value={survey.cable_conduit?.notes || ""}
                                    onBlur={(v) => patchSection("cable_conduit", { ...survey.cable_conduit, notes: v })}
                                    disabled={readOnly}
                                    testid="cable-notes"
                                />
                                <PhotoStrip
                                    label="Cable & conduit evidence"
                                    photos={sectionPhotos("cable_conduit")}
                                    onUpload={(file) => attachPhoto(file, { section: "cable_conduit" })}
                                    onRemove={removePhoto}
                                    onOpen={setShowLightbox}
                                    disabled={readOnly}
                                    testid="cable-photos"
                                />
                            </SectionCard>
                        )}

                        {activeSection === "equipment_location" && (
                            <SectionCard
                                title="Equipment location"
                                sublabel="Where the inverter and battery will physically live. Photograph both locations."
                                saving={saving === "equipment_location"}
                                proposed={proposed.equipment_location}
                            >
                                <FieldMissing missing={missingFor("equipment_location.proposed_inverter_location")}>
                                <TextFieldWithNA
                                    label="Proposed inverter location"
                                    value={survey.equipment_location?.proposed_inverter_location || ""}
                                    onBlur={(v) => patchSection("equipment_location", { ...survey.equipment_location, proposed_inverter_location: v })}
                                    disabled={readOnly}
                                    testid="equip-inverter-loc"
                                />
                                </FieldMissing>
                                <PhotoStrip
                                    label="Inverter location evidence"
                                    photos={sectionPhotos("equipment_location").filter((p) => p.evidence_category === "inverter")}
                                    onUpload={(file) => attachPhoto(file, { section: "equipment_location", evidence: "inverter" })}
                                    onRemove={removePhoto}
                                    onOpen={setShowLightbox}
                                    disabled={readOnly}
                                    testid="equip-inverter-photos"
                                />
                                <FieldMissing missing={missingFor("equipment_location.proposed_battery_location")}>
                                <TextFieldWithNA
                                    label="Proposed battery location"
                                    value={survey.equipment_location?.proposed_battery_location || ""}
                                    onBlur={(v) => patchSection("equipment_location", { ...survey.equipment_location, proposed_battery_location: v })}
                                    disabled={readOnly}
                                    testid="equip-battery-loc"
                                />
                                </FieldMissing>
                                <PhotoStrip
                                    label="Battery location evidence"
                                    photos={sectionPhotos("equipment_location").filter((p) => p.evidence_category === "battery")}
                                    onUpload={(file) => attachPhoto(file, { section: "equipment_location", evidence: "battery" })}
                                    onRemove={removePhoto}
                                    onOpen={setShowLightbox}
                                    disabled={readOnly}
                                    testid="equip-battery-photos"
                                />
                                <TextField
                                    label="Equipment location notes"
                                    multiline
                                    value={survey.equipment_location?.notes || ""}
                                    onBlur={(v) => patchSection("equipment_location", { ...survey.equipment_location, notes: v })}
                                    disabled={readOnly}
                                    testid="equip-notes"
                                />
                            </SectionCard>
                        )}

                        {activeSection === "additional_notes" && (
                            <SectionCard
                                title="Additional notes"
                                sublabel="Anything else Operations should know about the site."
                                saving={saving === "additional_notes"}
                            >
                                <TextField
                                    label="Additional notes"
                                    multiline
                                    rows={6}
                                    value={survey.additional_notes?.text || ""}
                                    onBlur={(v) => patchSection("additional_notes", { ...survey.additional_notes, text: v })}
                                    disabled={readOnly}
                                    testid="notes-text"
                                />
                                <PhotoStrip
                                    label="Additional evidence"
                                    photos={sectionPhotos("additional_notes")}
                                    onUpload={(file) => attachPhoto(file, { section: "additional_notes" })}
                                    onRemove={removePhoto}
                                    onOpen={setShowLightbox}
                                    disabled={readOnly}
                                    testid="notes-photos"
                                />
                            </SectionCard>
                        )}
                    </div>
                </div>

                {/* Resubmit modal */}
                {showResubmit && openClarification && (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="resubmit-modal">
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                            <div className="text-lg font-medium text-[#131B2F]">Resubmit for review</div>
                            <div className="text-sm text-[#4B5565] mt-1">Explain how you&rsquo;ve addressed the clarification request.</div>
                            <div className="mt-3 p-3 rounded-lg bg-[#FFF7ED] text-xs">
                                <div className="text-[#7C2D12] font-medium">Ops asked:</div>
                                <div className="text-[#131B2F] mt-1">{openClarification.text}</div>
                            </div>
                            <textarea
                                value={resubmitResponse}
                                onChange={(e) => setResubmitResponse(e.target.value)}
                                rows={4}
                                data-testid="resubmit-response"
                                placeholder="Your response…"
                                className="mt-3 w-full p-3 border border-[#E5E7EB] rounded-lg text-sm focus:outline-none focus:border-[#131B2F]"
                            />
                            <div className="mt-4 flex gap-2 justify-end">
                                <button onClick={() => setShowResubmit(false)} className="h-10 px-4 rounded-full text-sm border border-[#E5E7EB] bg-white text-[#131B2F]">Cancel</button>
                                <button
                                    onClick={resubmit}
                                    disabled={!resubmitResponse.trim()}
                                    data-testid="resubmit-confirm"
                                    className="h-10 px-4 rounded-full text-sm bg-[#131B2F] text-white disabled:opacity-50"
                                >
                                    Resubmit survey
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Submit confirmation modal */}
                {showSubmitConfirm && (
                    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="submit-confirm-modal">
                        <div className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl">
                            <div className="flex items-center gap-3 mb-3">
                                <span className="w-10 h-10 rounded-full bg-[#F1F8EC] text-[#3E9E2E] flex items-center justify-center">
                                    <ShieldCheck size={20} strokeWidth={1.75} />
                                </span>
                                <div>
                                    <div className="text-lg font-medium text-[#131B2F]">Submit site survey</div>
                                    <div className="text-sm text-[#4B5565]">Once submitted the survey is locked and passed to Operations for review.</div>
                                </div>
                            </div>
                            <p className="text-sm text-[#131B2F] my-4">
                                You&rsquo;re confirming, as <span className="font-medium">{project?.customer_name}</span>&rsquo;s
                                assigned engineer, that the captured findings accurately reflect the site.
                            </p>
                            <div className="flex gap-2 justify-end">
                                <button
                                    onClick={() => setShowSubmitConfirm(false)}
                                    disabled={submitting}
                                    data-testid="submit-cancel"
                                    className="h-10 px-4 rounded-full text-sm font-medium text-[#131B2F] bg-white border border-[#E5E7EB] hover:bg-[#F9FAFB] disabled:opacity-60"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={submit}
                                    disabled={submitting}
                                    data-testid="submit-confirm"
                                    className="h-10 px-4 rounded-full text-sm font-medium bg-[#3E9E2E] text-white hover:brightness-110 disabled:opacity-60"
                                >
                                    {submitting ? "Submitting…" : "Yes, submit survey"}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Lightbox */}
                {showLightbox && (
                    <div
                        className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 cursor-pointer"
                        onClick={() => setShowLightbox(null)}
                        data-testid="photo-lightbox"
                    >
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${photos.find((p) => p.id === showLightbox)?.download_url || `/api/documents/${showLightbox}/download`}`}
                            alt="Survey evidence"
                            className="max-w-full max-h-full rounded-lg"
                        />
                    </div>
                )}
            </div>
        </InstallerShell>
    );
}

// ─── Primitives ─────────────────────────────────────────────────────

function SectionCard({ title, sublabel, saving, proposed, children }) {
    return (
        <section className="pe-card p-5 md:p-6 space-y-4" data-testid={`section-card-${title.toLowerCase().replace(/\s+/g, "-")}`}>
            <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                    <h2 className="text-lg md:text-xl text-[#131B2F] font-medium">{title}</h2>
                    {sublabel && <div className="text-sm text-[#4B5565] mt-0.5">{sublabel}</div>}
                </div>
                {saving && (
                    <span className="inline-flex items-center gap-1 text-xs text-[#4B5565]" data-testid="section-saving">
                        <Loader2 size={12} className="animate-spin" /> Saving
                    </span>
                )}
            </div>
            {proposed && Object.keys(proposed || {}).length > 0 && (
                <div className="rounded-lg border border-dashed border-[#E5E7EB] bg-[#FAFBFC] p-3 text-xs">
                    <div className="uppercase tracking-[0.12em] text-[#4B5565] font-medium">Proposed</div>
                    <div className="text-[#131B2F] mt-1 whitespace-pre-wrap break-words">
                        {Object.entries(proposed).map(([k, v]) => (
                            <div key={k}><span className="text-[#4B5565]">{labelize(k)}:</span> {String(v ?? "—")}</div>
                        ))}
                    </div>
                </div>
            )}
            {children}
        </section>
    );
}

function MultiSelect({ options, value, onChange, disabled, testidPrefix }) {
    return (
        <div className="flex flex-wrap gap-2">
            {options.map((o) => {
                const on = value.includes(o);
                return (
                    <button
                        key={o}
                        type="button"
                        onClick={() => onChange(on ? value.filter((x) => x !== o) : [...value, o])}
                        disabled={disabled}
                        data-testid={`${testidPrefix}-${o}`}
                        className={"px-3 py-1.5 rounded-full text-sm border transition-colors " +
                            (on
                                ? "bg-[#131B2F] text-white border-[#131B2F]"
                                : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F9FAFB]") +
                            (disabled ? " opacity-60 cursor-not-allowed" : "")}
                    >
                        {labelize(o)}
                    </button>
                );
            })}
        </div>
    );
}

function Segmented({ label, options, value, onChange, disabled, testidPrefix }) {
    // DEV-010F · Options may be plain strings (`"yes"`) OR `[value, label]`
    // tuples (`["6mm_twin_earth", "6mm Twin & Earth"]`) so callers can
    // display a friendly label without moving away from the canonical
    // stored value.
    const norm = (o) => {
        if (Array.isArray(o)) return { value: o[0], label: o[1] };
        // DEV-010H UAT corrective pass · Render the "na" sentinel as
        // "N/A" so the Engineer sees an explicit non-applicable choice
        // rather than the raw string.
        if (o === "na") return { value: "na", label: "N/A" };
        return { value: o, label: labelize(o) };
    };
    return (
        <div>
            {label && <div className="text-sm font-medium text-[#131B2F] mb-1.5">{label}</div>}
            <div className="flex flex-wrap gap-2">
                {options.map((o) => {
                    const { value: v, label: l } = norm(o);
                    const on = value === v;
                    return (
                        <button
                            key={v}
                            type="button"
                            onClick={() => onChange(v)}
                            disabled={disabled}
                            data-testid={`${testidPrefix}-${v}`}
                            className={"px-3 py-1.5 rounded-full text-sm border transition-colors " +
                                (on
                                    ? "bg-[#131B2F] text-white border-[#131B2F]"
                                    : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F9FAFB]") +
                                (disabled ? " opacity-60 cursor-not-allowed" : "")}
                        >
                            {l}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

function TextField({ label, value, onBlur, multiline, rows = 3, disabled, testid }) {
    const [v, setV] = useState(value ?? "");
    useEffect(() => { setV(value ?? ""); }, [value]);
    const Cmp = multiline ? "textarea" : "input";
    return (
        <div>
            {label && <div className="text-sm font-medium text-[#131B2F] mb-1.5">{label}</div>}
            <Cmp
                value={v}
                onChange={(e) => setV(e.target.value)}
                onBlur={() => onBlur(v)}
                disabled={disabled}
                rows={multiline ? rows : undefined}
                data-testid={testid}
                className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm text-[#131B2F] focus:outline-none focus:border-[#131B2F] focus:ring-1 focus:ring-[#131B2F] disabled:bg-[#F9FAFB] disabled:cursor-not-allowed"
            />
        </div>
    );
}

function NumberField({ label, value, onBlur, disabled, testid, integer = false }) {
    const [v, setV] = useState(value != null ? String(value) : "");
    const [err, setErr] = useState("");
    // DEV-010F · If input was silently sanitised on the way in (e.g. a
    // paste of "1.5" is stripped to "15"), refuse to commit that value
    // on blur — the Engineer must retype the intended whole number.
    // Otherwise the spec's "do not silently persist invalid values"
    // rule would be violated.
    const [dirtySanitised, setDirtySanitised] = useState(false);
    useEffect(() => {
        setV(value != null ? String(value) : "");
        setErr(""); setDirtySanitised(false);
    }, [value]);

    const handleChange = (e) => {
        const raw = e.target.value;
        if (integer) {
            const sanitised = raw.replace(/[^\d]/g, "");
            setV(sanitised);
            if (sanitised !== raw) {
                setErr("Whole numbers only");
                setDirtySanitised(true);
            } else {
                setErr("");
                setDirtySanitised(false);
            }
        } else {
            setV(raw);
        }
    };
    const commit = () => {
        const trimmed = v.trim();
        if (trimmed === "") { onBlur(null); setErr(""); setDirtySanitised(false); return; }
        if (integer) {
            if (dirtySanitised) return;                 // keep error, don't persist
            if (!/^\d+$/.test(trimmed)) { setErr("Whole numbers only"); return; }
            const n = parseInt(trimmed, 10);
            if (Number.isNaN(n) || n < 0) { setErr("Whole numbers only"); return; }
            onBlur(n);
            setErr("");
            return;
        }
        const n = Number(trimmed);
        if (!Number.isNaN(n) && n >= 0) { onBlur(n); setErr(""); }
    };
    return (
        <div>
            {label && <div className="text-sm font-medium text-[#131B2F] mb-1.5">{label}</div>}
            <input
                type={integer ? "text" : "number"}
                inputMode={integer ? "numeric" : "decimal"}
                pattern={integer ? "[0-9]*" : undefined}
                min={0}
                value={v}
                onChange={handleChange}
                onKeyDown={integer ? (e) => {
                    if (["e", "E", "+", "-", ".", ","].includes(e.key)) e.preventDefault();
                } : undefined}
                onBlur={commit}
                disabled={disabled}
                data-testid={testid}
                className={"w-full px-3 py-2 border rounded-lg text-sm text-[#131B2F] focus:outline-none focus:ring-1 disabled:bg-[#F9FAFB] disabled:cursor-not-allowed " +
                    (err ? "border-[#B91C1C] focus:border-[#B91C1C] focus:ring-[#B91C1C]"
                         : "border-[#E5E7EB] focus:border-[#131B2F] focus:ring-[#131B2F]")}
            />
            {err && <div className="mt-1 text-xs text-[#B91C1C]" data-testid={`${testid}-error`}>{err}</div>}
        </div>
    );
}

/**
 * DEV-010H UAT corrective pass · Number field with an explicit N/A
 * companion flag. A numeric survey question is answered when EITHER
 *   • the field holds a real number (0 is fine), OR
 *   • the caller flips the companion `<field>_na` boolean.
 * Blank is never treated as N/A.
 */
function NumberFieldWithNA({ label, value, naValue, onChange, disabled, testid, integer = false }) {
    return (
        <div>
            {label && <div className="text-sm font-medium text-[#131B2F] mb-1.5">{label}</div>}
            <div className={naValue ? "opacity-50 pointer-events-none" : ""}>
                <NumberField
                    label={null}
                    value={value}
                    onBlur={(n) => onChange({ value: n, na: false })}
                    disabled={disabled || naValue}
                    testid={testid}
                    integer={integer}
                />
            </div>
            <label className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-[#4B5565] cursor-pointer">
                <input
                    type="checkbox"
                    checked={!!naValue}
                    onChange={(e) => onChange({ value: e.target.checked ? null : value, na: e.target.checked })}
                    disabled={disabled}
                    data-testid={`${testid}-na`}
                />
                Mark as N/A (not applicable)
            </label>
        </div>
    );
}

/**
 * DEV-010H UAT corrective pass · Text field with an "N/A" chip. Sets the
 * value to the canonical "na" sentinel so the completion engine treats
 * the field as explicitly answered.
 */
function TextFieldWithNA({ label, value, onBlur, disabled, testid, multiline, rows }) {
    const isNa = String(value || "").trim() === "na";
    return (
        <div>
            {label && (
                <div className="flex items-center justify-between mb-1.5">
                    <div className="text-sm font-medium text-[#131B2F]">{label}</div>
                    <button
                        type="button"
                        onClick={() => onBlur(isNa ? "" : "na")}
                        disabled={disabled}
                        data-testid={`${testid}-na`}
                        className={"h-6 px-2 rounded-full text-[11px] border " +
                            (isNa ? "bg-[#131B2F] text-white border-[#131B2F]"
                                  : "bg-white text-[#131B2F] border-[#E5E7EB] hover:bg-[#F9FAFB]") +
                            (disabled ? " opacity-60 cursor-not-allowed" : "")}
                    >
                        N/A
                    </button>
                </div>
            )}
            {isNa ? (
                <div className="w-full px-3 py-2 border border-[#E5E7EB] rounded-lg text-sm text-[#4B5565] bg-[#F9FAFB]" data-testid={`${testid}-na-display`}>
                    Not applicable
                </div>
            ) : (
                <TextField
                    label={null}
                    value={value}
                    onBlur={onBlur}
                    disabled={disabled}
                    testid={testid}
                    multiline={multiline}
                    rows={rows}
                />
            )}
        </div>
    );
}

function PhotoStrip({ label, photos, onUpload, onRemove, onOpen, disabled, testid }) {
    const inputRef = useRef(null);
    // DEV-010E · Local busy state so the Add Photo control disables the
    // instant an upload starts. We track it here (not at page level) so
    // multiple PhotoStrips can upload independently and the parent's
    // section-save spinner logic is unaffected. Duplicate submissions
    // caused by rapid re-tapping are also prevented via the disabled flag.
    const [uploading, setUploading] = useState(false);
    const handlePick = async (file) => {
        if (!file || uploading || disabled) return;
        setUploading(true);
        try {
            await onUpload(file);
        } finally {
            setUploading(false);
        }
    };
    return (
        <div>
            <div className="text-sm font-medium text-[#131B2F] mb-1.5">{label}</div>
            <div className="flex flex-wrap gap-2 items-center" data-testid={testid}>
                {photos.map((p) => (
                    <div key={p.id} className="relative group" data-testid={`photo-${p.id}`}>
                        <img
                            src={`${process.env.REACT_APP_BACKEND_URL}${p.download_url || `/api/documents/${p.id}/download`}`}
                            alt={p.title || "Survey photo"}
                            onClick={() => onOpen && onOpen(p.id)}
                            className="w-20 h-20 rounded-lg object-cover border border-[#E5E7EB] cursor-pointer hover:opacity-90 bg-[#F1F5F9]"
                        />
                        {!disabled && (
                            <button
                                type="button"
                                onClick={() => onRemove(p.id)}
                                data-testid={`photo-remove-${p.id}`}
                                className="absolute -top-1.5 -right-1.5 w-6 h-6 rounded-full bg-white border border-[#E5E7EB] text-[#B91C1C] shadow-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                title="Remove"
                            >
                                <Trash2 size={11} strokeWidth={2} />
                            </button>
                        )}
                    </div>
                ))}
                {!disabled && (
                    <>
                        <button
                            type="button"
                            onClick={() => !uploading && inputRef.current?.click()}
                            disabled={uploading}
                            data-testid={`${testid}-upload`}
                            aria-busy={uploading}
                            className={`w-20 h-20 rounded-lg border-2 border-dashed flex flex-col items-center justify-center gap-1 transition-colors ${
                                uploading
                                    ? "border-[#131B2F] text-[#131B2F] bg-[#F9FAFB] cursor-not-allowed"
                                    : "border-[#E5E7EB] text-[#4B5565] hover:border-[#131B2F] hover:text-[#131B2F]"
                            }`}
                        >
                            {uploading ? (
                                <>
                                    <Loader2 size={16} strokeWidth={1.75} className="animate-spin" />
                                    <span className="text-[10px]">Uploading…</span>
                                </>
                            ) : (
                                <>
                                    <Camera size={16} strokeWidth={1.75} />
                                    <span className="text-[10px]">Add photo</span>
                                </>
                            )}
                        </button>
                        <input
                            ref={inputRef}
                            type="file"
                            accept="image/*"
                            capture="environment"
                            hidden
                            onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = "";
                                if (f) handlePick(f);
                            }}
                        />
                    </>
                )}
            </div>
        </div>
    );
}

function ElevationCard({ elevation: e, index, onUpdate, onRemove, photos, onPhoto, onPhotoRemove, onPhotoOpen, readOnly, missingFor }) {
    // DEV-010H UAT corrective pass 2 · Elevation-scoped missing lookup.
    // Falls back to a no-op when the parent hasn't provided one.
    const mF = missingFor || (() => null);
    // DEV-010G · Explicit Save elevation.
    //
    // Field edits now buffer into a local draft rather than firing the
    // API on every blur. The Engineer commits with a single Save
    // action, which surfaces a clear saving → saved → failed lifecycle
    // so it is unambiguous that the elevation has been captured.
    // The parent still owns the elevation state; on save success we
    // hand the merged patch up via `onUpdate` (which persists + refreshes).
    const [draft, setDraft] = useState(e);
    const [status, setStatus] = useState("saved");   // clean-ish for existing rows
    const [err, setErr] = useState(null);

    // Reset the draft when the parent's elevation changes (e.g. after
    // a successful save that reloads elevations from the API).
    useEffect(() => { setDraft(e); setErr(null); }, [e.id, e.updated_at]);   // eslint-disable-line

    const edit = (patch) => {
        if (readOnly) return;
        setDraft((d) => ({ ...d, ...patch }));
        setStatus("dirty");
        setErr(null);
    };

    const save = async () => {
        if (readOnly) return;
        setStatus("saving");
        setErr(null);
        try {
            // Only send fields we hold a value for.
            const patch = {
                name:              draft.name,
                orientation:       draft.orientation,
                roof_covering:     draft.roof_covering,
                proposed_panel_qty: draft.proposed_panel_qty,
                surveyed_panel_qty: draft.surveyed_panel_qty,
                measurements:      draft.measurements,
                access_constraints: draft.access_constraints,
                scaffolding_required: draft.scaffolding_required,
                scaffolding_notes: draft.scaffolding_notes,
                notes:             draft.notes,
            };
            await onUpdate(patch);
            setStatus("saved");
        } catch (ex) {
            setStatus("error");
            setErr(formatApiError(ex));
        }
    };

    const statusBadge = (() => {
        if (readOnly) return null;
        if (status === "dirty")  return <span className="text-xs text-[#B45309]" data-testid={`elevation-${e.id}-status-dirty`}>Unsaved changes</span>;
        if (status === "saving") return <span className="text-xs text-[#4B5565] inline-flex items-center gap-1" data-testid={`elevation-${e.id}-status-saving`}><Loader2 size={12} className="animate-spin" /> Saving…</span>;
        if (status === "error")  return <span className="text-xs text-[#B91C1C]" data-testid={`elevation-${e.id}-status-error`}>Save failed</span>;
        return <span className="text-xs text-[#166534] inline-flex items-center gap-1" data-testid={`elevation-${e.id}-status-saved`}><Check size={12} strokeWidth={2.5} /> Saved</span>;
    })();

    return (
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-4 space-y-3" data-testid={`elevation-${e.id}`} data-elevation-id={e.id}>
            <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                <FieldMissing missing={mF("name")}>
                <TextField
                    label="Elevation name"
                    value={draft.name || `Elevation ${index + 1}`}
                    onBlur={(v) => edit({ name: v })}
                    disabled={readOnly}
                    testid={`elevation-${e.id}-name`}
                />
                </FieldMissing>
                </div>
                {!readOnly && (
                    <button
                        type="button"
                        onClick={onRemove}
                        data-testid={`elevation-${e.id}-remove`}
                        className="shrink-0 self-end mb-0.5 h-9 px-3 rounded-full text-sm text-[#B91C1C] bg-white border border-[#FEE2E2] hover:bg-[#FEF2F2]"
                    >
                        <Trash2 size={13} strokeWidth={1.75} className="inline mr-1" /> Remove
                    </button>
                )}
            </div>
            <FieldMissing missing={mF("orientation")}>
            <Segmented
                label="Orientation"
                options={ORIENTATIONS}
                value={draft.orientation}
                onChange={(v) => edit({ orientation: v })}
                disabled={readOnly}
                testidPrefix={`elevation-${e.id}-orient`}
            />
            </FieldMissing>
            <FieldMissing missing={mF("roof_covering")}>
            <Segmented
                label="Roof covering"
                options={ROOFS}
                value={draft.roof_covering}
                onChange={(v) => edit({ roof_covering: v })}
                disabled={readOnly}
                testidPrefix={`elevation-${e.id}-roof`}
            />
            </FieldMissing>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <NumberField
                    label="Proposed panel qty"
                    integer
                    value={draft.proposed_panel_qty}
                    onBlur={(n) => edit({ proposed_panel_qty: n })}
                    disabled={readOnly}
                    testid={`elevation-${e.id}-proposed`}
                />
                <FieldMissing missing={mF("surveyed_panel_qty")}>
                <NumberField
                    label="Surveyed panel qty"
                    integer
                    value={draft.surveyed_panel_qty}
                    onBlur={(n) => edit({ surveyed_panel_qty: n })}
                    disabled={readOnly}
                    testid={`elevation-${e.id}-surveyed`}
                />
                </FieldMissing>
            </div>
            <FieldMissing missing={mF("measurements")}>
            <TextFieldWithNA
                label="Measurements"
                value={draft.measurements || ""}
                onBlur={(v) => edit({ measurements: v })}
                disabled={readOnly}
                testid={`elevation-${e.id}-measurements`}
            />
            </FieldMissing>
            <FieldMissing missing={mF("access_constraints")}>
            <TextFieldWithNA
                label="Access constraints"
                value={draft.access_constraints || ""}
                onBlur={(v) => edit({ access_constraints: v })}
                disabled={readOnly}
                testid={`elevation-${e.id}-access`}
            />
            </FieldMissing>
            <FieldMissing missing={mF("scaffolding_required")}>
            <Segmented
                label="Scaffolding required"
                options={SCAFFOLDING_OPTS}
                value={draft.scaffolding_required}
                onChange={(v) => edit({ scaffolding_required: v })}
                disabled={readOnly}
                testidPrefix={`elevation-${e.id}-scaffold`}
            />
            </FieldMissing>
            {draft.scaffolding_required === "yes" && (
                <TextField
                    label="Scaffolding notes"
                    value={draft.scaffolding_notes || ""}
                    onBlur={(v) => edit({ scaffolding_notes: v })}
                    disabled={readOnly}
                    testid={`elevation-${e.id}-scaffold-notes`}
                />
            )}
            <TextField
                label="Elevation notes"
                multiline
                value={draft.notes || ""}
                onBlur={(v) => edit({ notes: v })}
                disabled={readOnly}
                testid={`elevation-${e.id}-notes`}
            />
            <PhotoStrip
                label="Elevation evidence"
                photos={photos}
                onUpload={onPhoto}
                onRemove={onPhotoRemove}
                onOpen={onPhotoOpen}
                disabled={readOnly}
                testid={`elevation-${e.id}-photos`}
            />
            {!readOnly && (
                <div className="flex items-center justify-between pt-2 border-t border-[#F1F5F9]">
                    <div>{statusBadge}{err && <div className="text-xs text-[#B91C1C] mt-1">{err}</div>}</div>
                    <button
                        type="button"
                        onClick={save}
                        disabled={status === "saving" || status === "saved"}
                        data-testid={`elevation-${e.id}-save`}
                        className={"inline-flex items-center gap-1.5 h-9 px-4 rounded-full text-sm font-medium " +
                            (status === "saved"
                                ? "bg-[#F1F8EC] text-[#166534] border border-[#DCFCE7] cursor-not-allowed"
                                : status === "saving"
                                    ? "bg-[#131B2F] text-white opacity-70 cursor-wait"
                                    : "bg-[#131B2F] text-white hover:brightness-110")}
                    >
                        {status === "saving" ? (<><Loader2 size={13} className="animate-spin" /> Saving…</>)
                            : status === "saved" ? (<><Check size={13} strokeWidth={2.5} /> Saved</>)
                            : "Save elevation"}
                    </button>
                </div>
            )}
        </div>
    );
}
